<template>
    <master-layout>

        <!-- Breadcamb -->
        <div class="breadcamb_box">
            <div>
                <h4 class="page-title"><i class="ti-bar-chart"></i>{{ $t('follow_ups_report') }}</h4>
            </div>
            <pml-back-button></pml-back-button>
        </div>

        <div class="card-box">
            <form @submit.prevent="refreshCharts">
                <div class="row">
                    <div class="col-xs-12 col-sm-6 m-b-15 clearfix">
                        <label>{{ $t('date_range') }}</label>
                        <div class="input-daterange input-group">
                            <pml-date-picker v-model="filters.created_at_from"></pml-date-picker>
                            <span class="input-group-addon b-0">
                                <i class="fa fa-calendar"></i>
                            </span>
                            <pml-date-picker v-model="filters.created_at_to"></pml-date-picker>
                        </div>
                    </div>
                    <div class="col-xs-12 col-sm-6 m-b-15 clearfix">
                        <label>{{ $t('consultant') }}</label>
                        <pml-select-members v-model="filters.member_id" :options="members" :onlyActive="true"></pml-select-members>
                    </div>
                    <div class="col-xs-12 col-sm-6 m-b-15 clearfix">
                        <label>{{ $t('access_ways') }}</label>
                        <pml-select v-model="filters.access_way_id" :options="accessWays"></pml-select>
                    </div>
                    <div class="col-xs-12 col-sm-6 m-b-15 clearfix">
                        <label>{{ $t('segments') }}</label>
                        <pml-select v-model="filters.segment_id" :options="segments"></pml-select>
                    </div>
                    <div class="col-xs-12 m-b-15 clearfix" v-if="filters.show_log">
                        <pml-checkbox v-model="filters.show_log" :label="$t('show_follow_up_logs')"></pml-checkbox>
                    </div>
                </div>
                <hr class="m-t-0">
                <div class="clearfix">
                    <span class="btn btn-white pull-left" @click.prevent="clear"><i class="fa fa-paint-brush m-r-5"></i> {{ $t('clear') }}</span>
                    <button class="btn btn_green pull-right" type="submit">
                        <span class="btn-label">
                            <i class="fa fa-search"></i>
                        </span>
                        {{ $t('filter') }}
                    </button>
                </div>
            </form>
        </div>
        <div class="card-box">
            <h4 class="header-title m-t-0 m-b-20 text-dark">{{ $t('follow_ups_by_consultant') }}</h4>
            <div v-pml-block="isLoadingStacked">
                <div class="graph_task" v-show="hasStackedRegistry" :style="`height: ${default_projection_height}px; max-height: ${default_projection_height}px;`" ref="projectionChart"></div>
                <pml-ec-bar v-if="!hasStackedRegistry"></pml-ec-bar>
            </div>
        </div>
        <div class="row">
            <div class="col-xs-12 col sm-12 col-md-8">
                <div class="card-box p-0">
                    <div class="p-10">
                        <h4 class="header-title m-t-10 m-b-5 text-dark t_sm_center t_overflow">{{ $t('follow_ups_by_consultant') }}</h4>
                    </div>
                    <div class="over_auto">
                        <div v-pml-block="isLoadingStacked" style="min-height: 72px;">
                            <table class="table table-striped table-bordered m-0 table_hover_link">
                                <thead>
                                    <tr>
                                        <th class="min_w_180">{{ $t(stackedRegistry.consultants[0].name) }}</th>
                                        <th class="t_center" v-for="row in stackedRegistry.access_ways">{{ row.values[0] }}</th>
                                        <th class="t_center">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <router-link
                                        tag="tr"
                                        :to="`/reports/follow-ups/timeline/${stackedRegistry.consultants[n].id}`"
                                        v-for="n in (stackedRegistry.consultants.length - 1)"
                                        :key="stackedRegistry.consultants[n].id"
                                    >
                                        <td class="bold">{{ stackedRegistry.consultants[n].name }}</td>
                                        <td
                                            class="t_center"
                                            v-for="row in stackedRegistry.access_ways"
                                            :key="stackedRegistry.consultants[n].id + row.id"
                                        >
                                            {{ row.values[n] }}
                                        </td>
                                        <td class="t_center bold">{{ stackedRegistry.totals[n - 1] }}</td>

                                    </router-link>
                                    <tr v-if="!hasStackedRegistry">
                                        <td class="t_center" colspan="30"><i class="fa fa-exclamation-triangle"></i> {{ $t('no_records_found') }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xs-12 col sm-12 col-md-4">
                <div class="card-box">
                    <h4 class="header-title m-t-0 m-b-20 text-dark">{{ $t('follow_ups_by_type') }}</h4>
                    <div v-pml-block="isLoadingPie" style="min-height: 200px;">
                        <div style="height: 280px;" ref="perceptionChart" v-show="hasPieRegistry"></div>
                        <pml-ec-pizza v-if="!hasPieRegistry"></pml-ec-pizza>
                    </div>
                </div>
            </div>
        </div>
    </master-layout>
</template>

<script>
    import c3 from 'c3'
    import * as d3 from 'd3'
    import { getFollowUpsReport } from 'services/reports'
    import { groupBy, map, orderBy, isNil, isEmpty } from 'lodash'
    import { mapGetters } from 'vuex'
    import moment from 'moment'
    import { trans } from 'utils/helpers/translation'

    export default {
        name: 'FollowUpsReport',
        data () {
            return {
                isLoadingStacked: false,
                isLoadingPie: false,
                refreshed: true,
                projection: {},
                perception: {},
                count: {},
                default_projection_height: null,
                filters: {
                    created_at_from: null,
                    created_at_to: null,
                    show_log: false,
                    member_id: null,
                    access_way_id: null,
                    segment_id: null
                },
                stackedRegistry: {
                    totals: [],
                    consultants: [{name: 'consultant'}],
                    access_ways: [],
                    colors: {},
                    groups: []
                },
                pieRegistry: {
                    columns: [],
                    colors: []
                }
            }
        },
        metaInfo () {
            return {
                title: trans('follow_ups_report')
            }
        },
        mounted () {
            this.initFilters()
            this.refreshCharts()
        },
        computed: {
            ...mapGetters({
                'segments': 'getSegments',
                'accessWays': 'getAccessWays',
                'members': 'getMembers'
            }),
            hasStackedRegistry () {
                return this.stackedRegistry.consultants.length > 1
            },
            hasPieRegistry () {
                return this.pieRegistry.columns.length !== 0
            }
        },
        watch: {
            'stackedRegistry.consultants' (val) {
                this.default_projection_height = val.length * 30 + 60
                setTimeout(() => { this.plotStackedBarChart() }, 100)
            },
            'pieRegistry.columns' () {
                setTimeout(() => { this.plotPieChart() }, 100)
            }
        },
        methods: {
            initFilters () {
                this.filters = {
                    created_at_from: this.storeFormat(window.localStorage.getItem('pml_reports_follow_ups_created_at_from'), 'created_at_from'),
                    created_at_to: this.storeFormat(window.localStorage.getItem('pml_reports_follow_ups_created_at_to'), 'created_at_to'),
                    show_log: this.storeFormat(window.localStorage.getItem('pml_reports_follow_ups_show_log'), 'show_log'),
                    member_id: this.storeFormat(window.localStorage.getItem('pml_reports_follow_ups_member_id'), 'member_id'),
                    access_way_id: this.storeFormat(window.localStorage.getItem('pml_reports_follow_ups_access_way_id'), 'access_way_id'),
                    segment_id: this.storeFormat(window.localStorage.getItem('pml_reports_follow_ups_segment_id'), 'segment_id')
                }
            },
            updateStorage () {
                window.localStorage.setItem('pml_reports_follow_ups_created_at_from', isNil(this.filters.created_at_from) ? '' : this.filters.created_at_from)
                window.localStorage.setItem('pml_reports_follow_ups_created_at_to', isNil(this.filters.created_at_to) ? '' : this.filters.created_at_to)
                window.localStorage.setItem('pml_reports_follow_ups_show_log', this.filters.show_log ? '1' : '0')
                window.localStorage.setItem('pml_reports_follow_ups_member_id', isNil(this.filters.member_id) ? '' : this.filters.member_id)
                window.localStorage.setItem('pml_reports_follow_ups_access_way_id', isNil(this.filters.access_way_id) ? '' : this.filters.access_way_id)
                window.localStorage.setItem('pml_reports_follow_ups_segment_id', isNil(this.filters.segment_id) ? '' : this.filters.segment_id)
            },
            storeFormat (val, type) {
                if (type === 'show_log')
                    return val === 1
                if ((isNil(val) || isEmpty(val)) && type === 'created_at_from')
                    return moment().subtract(14, 'days').format('DD/MM/YYYY')
                if ((isNil(val) || isEmpty(val)) && type === 'created_at_to')
                    return moment().add(1, 'days').format('DD/MM/YYYY')
                if ((isNil(val) || isEmpty(val)) && (type === 'member_id' || type === 'access_way_id' || type === 'segment_id'))
                    return null
                if (type === 'member_id' || type === 'access_way_id' || type === 'segment_id')
                    return parseInt(val)
                if (type === 'created_at_from' || type === 'created_at_to')
                    return moment(val, 'DD/MM/YYYY').format('DD/MM/YYYY')
            },
            refreshCharts () {
                this.refreshed = true
                this.initStackedBar()
                this.initPie()
                this.updateStorage()
            },
            initStackedBar () {
                this.isLoadingStacked = true
                getFollowUpsReport({...this.filters, report_type: 'stacked_bar'})
                    .then((response) => {
                        this.fetchStackedBarData(response.data)
                    })
                    .catch((error) => { console.log(error) })
                    .then(() => { this.isLoadingStacked = false })
            },
            initPie () {
                this.isLoadingPie = true
                getFollowUpsReport({...this.filters, report_type: 'pie'})
                    .then((response) => {
                        this.fetchPieData(response.data)
                    })
                    .catch((error) => { console.log(error) })
                    .then(() => { this.isLoadingPie = false })
            },
            plotStackedBarChart () {
                let showLastLabel = () => {
                    let shown = chart.data.shown().map(function (item) {
                        return item.id
                    }) // get visible ids: ['data1', 'data2', ...]
                    let last = (shown[shown.length - 1])
                    d3.select('.c3-chart-texts').selectAll('.c3-target').style('display', 'none') // hide all
                    d3.select('.c3-chart-texts').selectAll('.c3-target-' + last).style('display', 'block') // show last
                }
                let totals = []
                let chart = c3.generate({
                    bindto: this.$refs.projectionChart,
                    padding: {
                        left: 100
                    },
                    legend: {
                        item: {
                            onclick: (id) => {
                                chart.toggle([id]) // keep default functionality
                                showLastLabel()
                            }
                        }
                    },
                    onrendered: () => {
                        if (this.refreshed) {
                            this.refreshed = false
                            this.stackedRegistry.totals = totals
                        }
                        totals = []
                    },
                    data: {
                        labels: {
                            format: (v, id, i, j) => {
                                if (isNaN(totals[i]))
                                    totals[i] = 0
                                // sum only visible
                                if (chart) {
                                    let shown = chart.data.shown().map((item) => { return item.id })
                                    if (shown.indexOf(id) !== -1)
                                        totals[i] += v
                                } else {
                                    totals[i] += v
                                }

                                return totals[i]
                            }
                        },
                        x: 'consultant',
                        columns: [
                            map(this.stackedRegistry.consultants, (item) => item.name),
                            ...map(this.stackedRegistry.access_ways, (item) => item.values)
                        ],
                        colors: this.stackedRegistry.colors,
                        type: 'bar',
                        order: null, // disable reordering of stack
                        groups: [this.stackedRegistry.groups]
                    },
                    bar: {
                        width: 20
                    },
                    axis: {
                        rotated: true,
                        x: {
                            type: 'category'
                        }
                    }
                })
                showLastLabel()
            },
            plotPieChart () {
                c3.generate({
                    bindto: this.$refs.perceptionChart,
                    data: {
                        columns: this.pieRegistry.columns,
                        type: 'pie'
                    },
                    color: {
                        pattern: this.pieRegistry.colors
                    },
                    pie: {
                        label: {
                            show: false
                        }
                    },
                    legend: {
                        position: 'bottom'
                    },
                    tooltip: {
                        format: {
                            value (value, ratio, id, index) {
                                return `&nbsp;${d3.format('.1%')(ratio)} (${value})`
                            }
                        }
                    }
                })
            },
            fetchStackedBarData (data) {
                let groupedCons = groupBy(data, (item) => item.member_id)
                let consIds = orderBy(Object.getOwnPropertyNames(groupedCons), (member) => this.$options.filters.pmlMemberById(member, 'full_name'))
                let consNames = map(consIds, (member) => ({id: member, name: this.$options.filters.pmlMemberById(member, 'full_name')}))
                consNames.splice(0, 0, {name: 'consultant'})

                this.stackedRegistry.consultants = consNames

                for (let consId of consIds)
                    groupedCons[consId] = groupBy(groupedCons[consId], (item) => item.access_way_id)

                let awColumns = []
                let awColors = {}
                for (let aw of [...this.accessWays, {id: null, name: 'PumpMyLead', color: '#8338b1'}]) {
                    let empty = true
                    let awValue = [aw.name]
                    awColors[aw.name] = aw.color
                    for (let consId of consIds)
                        if (groupedCons[consId][aw.id] !== undefined) {
                            empty = false
                            awValue.push(groupedCons[consId][aw.id][0].count)
                        } else {
                            awValue.push(0)
                        }

                    if (!empty)
                        awColumns.push({id: aw.id, values: awValue})
                }

                let groups = Object.getOwnPropertyNames(awColors)

                this.stackedRegistry.access_ways = awColumns
                this.stackedRegistry.colors = awColors
                this.stackedRegistry.groups = groups
            },
            fetchPieData (data) {
                this.pieRegistry.columns = map(data, (item) => {
                    if (item.access_way_id !== null)
                        return [this.$options.filters.pmlAccessWayById(item.access_way_id, 'name'), item.count]
                    return ['PumpMyLead', item.count]
                })
                this.pieRegistry.colors = map(data, (item) => {
                    if (item.access_way_id !== null)
                        return this.$options.filters.pmlAccessWayById(item.access_way_id, 'color')
                    return '#8338b1'
                })
            },
            clear () {
                this.filters = {
                    created_at_from: moment().subtract(14, 'days').format('DD/MM/YYYY'),
                    created_at_to: moment().add(1, 'days').format('DD/MM/YYYY'),
                    show_log: false,
                    member_id: null,
                    access_way_id: null,
                    segment_id: null
                }
                this.refreshCharts()
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/modules/reports/follow-ups/components/Main.vue